import { ConnectError } from "@connectrpc/connect";
import type {
  MultiPayment,
  MultiPaymentElementChangeEvent,
} from "@mul-pay/mptoken-js";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useMultiPayment,
} from "@mul-pay/mptoken-react-js";
import { redirect, useNavigate } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import toast from "react-hot-toast";
import ArrowBack from "~icons/material-symbols/arrow-back";
import ProgressActivity from "~icons/material-symbols/progress-activity";
import { css, cx } from "../../../../styled-system/css";
import Header from "../../../components/header";
import { CardBrand } from "../../../features/charge/card-ui";
import { getCreditCardsOrEmpty } from "../../../features/charge/credit-card-loader";
import { showGMOPGErrorToast } from "../../../features/charge/gmopg-error-toast";
import {
  cardElementOptions,
  createCardToken,
  initialCardElementStatuses,
  loadMulpayForCardToken,
} from "../../../features/charge/mulpay";
import { setPendingRegisteredCreditCard } from "../../../features/charge/pending-credit-card";
import {
  fieldCss,
  helperTextCss,
  inputCss,
  labelCss,
  pageCss,
  primaryButtonCss,
  secondaryButtonCss,
} from "../../../features/charge/styles";
import {
  type TenantWithFeature,
  extractValidationViolationMessages,
  getGMOPGErrorInfoFromUnknown,
} from "../../../libs/connect";
import { t } from "../../../libs/i18n";
import { getBillingProfileWithCache } from "../../../libs/sdk";

type ChargeSearch = {
  amount?: number;
};

type LoaderData = {
  initialBillingAddress: {
    billingName: string;
    state: string;
    city: string;
  };
  isFirstCard: boolean;
};

type CardForm = {
  holderName: string;
  setAsDefault: boolean;
  billingName: string;
  postCode: string;
  state: string;
  city: string;
  line1: string;
  line2: string;
};

const initialForm: CardForm = {
  holderName: "",
  setAsDefault: true,
  billingName: "",
  postCode: "",
  state: "",
  city: "",
  line1: "",
  line2: "",
};

const postCodePattern = "^[0-9]{7}$";
const cardHolderNamePattern = "^[A-Z ]+$";

const addCreditCardValidationMessages: Record<string, string> = {
  "billing_address.name": t("氏名を入力してください。"),
  "billing_address.line1": t("住所を入力してください。"),
  "billing_address.city": t("市区町村を入力してください。"),
  "billing_address.state": t("都道府県を選択してください。"),
  "billing_address.post_code": t("郵便番号は半角数字7桁で入力してください。"),
};

const prefectures = [
  ["01", "北海道"],
  ["02", "青森県"],
  ["03", "岩手県"],
  ["04", "宮城県"],
  ["05", "秋田県"],
  ["06", "山形県"],
  ["07", "福島県"],
  ["08", "茨城県"],
  ["09", "栃木県"],
  ["10", "群馬県"],
  ["11", "埼玉県"],
  ["12", "千葉県"],
  ["13", "東京都"],
  ["14", "神奈川県"],
  ["15", "新潟県"],
  ["16", "富山県"],
  ["17", "石川県"],
  ["18", "福井県"],
  ["19", "山梨県"],
  ["20", "長野県"],
  ["21", "岐阜県"],
  ["22", "静岡県"],
  ["23", "愛知県"],
  ["24", "三重県"],
  ["25", "滋賀県"],
  ["26", "京都府"],
  ["27", "大阪府"],
  ["28", "兵庫県"],
  ["29", "奈良県"],
  ["30", "和歌山県"],
  ["31", "鳥取県"],
  ["32", "島根県"],
  ["33", "岡山県"],
  ["34", "広島県"],
  ["35", "山口県"],
  ["36", "徳島県"],
  ["37", "香川県"],
  ["38", "愛媛県"],
  ["39", "高知県"],
  ["40", "福岡県"],
  ["41", "佐賀県"],
  ["42", "長崎県"],
  ["43", "熊本県"],
  ["44", "大分県"],
  ["45", "宮崎県"],
  ["46", "鹿児島県"],
  ["47", "沖縄県"],
] as const;

export const Route = createFileRoute("/_authed/charge/credit-card/add")({
  validateSearch: (search: Record<string, unknown>): ChargeSearch => ({
    amount: Number(search.amount) === 10000 ? 10000 : 5000,
  }),
  beforeLoad: ({ context: { tenant } }) => {
    if (!tenant.feature.charge) {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context: { client } }) => {
    const [initialBillingAddress, { response: creditCardsResponse }] =
      await Promise.all([
        getInitialBillingAddress(),
        getCreditCardsOrEmpty(client),
      ]);

    return {
      initialBillingAddress,
      isFirstCard: Object.keys(creditCardsResponse.cards).length === 0,
    } satisfies LoaderData;
  },
  component: AddCreditCard,
});

function AddCreditCard() {
  const { tenant } = Route.useRouteContext();
  const [mulpayResetKey, setMulpayResetKey] = useState(0);

  return (
    <MulpayElementsProvider key={mulpayResetKey} tenantSlug={tenant.slug}>
      <AddCreditCardContent
        onResetMulpay={() => setMulpayResetKey((key) => key + 1)}
      />
    </MulpayElementsProvider>
  );
}

type AddCreditCardContentProps = {
  onResetMulpay: () => void;
};

function AddCreditCardContent({ onResetMulpay }: AddCreditCardContentProps) {
  const { amount = 5000 } = Route.useSearch();
  const { client, tenant } = Route.useRouteContext();
  const { initialBillingAddress, isFirstCard } = Route.useLoaderData();
  const navigate = useNavigate();
  const elements = useElements();
  const multiPayment = useMultiPayment();
  const [form, setForm] = useState<CardForm>(() => ({
    ...initialForm,
    billingName: initialBillingAddress.billingName,
    state: initialBillingAddress.state,
    city: initialBillingAddress.city,
  }));
  const [cardElementStatuses, setCardElementStatuses] = useState(
    initialCardElementStatuses,
  );
  const [loading, setLoading] = useState(false);
  const setAsDefault = isFirstCard || form.setAsDefault;

  const update =
    (key: keyof CardForm) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value =
        event.target.type === "checkbox"
          ? (event.target as HTMLInputElement).checked
          : event.target.value;
      setForm((current) => ({ ...current, [key]: value }));
    };

  const updateHolderName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const holderName = event.target.value
      .normalize("NFKC")
      .toUpperCase()
      .replace(/[^A-Z ]/g, "")
      .replace(/ {2,}/g, " ")
      .trimStart();

    setForm((current) => ({ ...current, holderName }));
  };

  const updatePostCode = (event: React.ChangeEvent<HTMLInputElement>) => {
    const postCode = event.target.value
      .normalize("NFKC")
      .replace(/[^0-9]/g, "")
      .slice(0, 7);

    setForm((current) => ({ ...current, postCode }));
  };

  const updateCardElementStatus = (event: MultiPaymentElementChangeEvent) => {
    setCardElementStatuses((current) => ({
      ...current,
      [event.elementType]: {
        complete: event.complete,
        errorMessage: event.error?.message,
      },
    }));
  };

  const holderName = form.holderName.trim();
  const hasValidHolderName =
    holderName.length > 0 && new RegExp(cardHolderNamePattern).test(holderName);
  const hasValidBillingAddress =
    form.billingName.trim().length > 0 &&
    new RegExp(postCodePattern).test(form.postCode) &&
    form.state.length > 0 &&
    form.city.trim().length > 0 &&
    form.line1.trim().length > 0;
  const canSubmit =
    !loading &&
    !!multiPayment &&
    !!elements &&
    hasValidHolderName &&
    hasValidBillingAddress &&
    Object.values(cardElementStatuses).every((status) => status.complete);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    let shouldResetMulpay = false;

    try {
      const token = await createCardToken({
        multiPayment,
        elements,
        holderName: form.holderName,
      }).catch((error: unknown) => {
        shouldResetMulpay = true;
        throw error;
      });

      const response = await client.addCreditCard({
        token,
        callbackUrlPath: `/charge/credit?amount=${amount}`,
        setAsDefault,
        billingAddress: {
          name: form.billingName,
          postCode: form.postCode,
          state: form.state,
          city: form.city,
          line1: form.line1,
          line2: form.line2 || undefined,
        },
      });

      setPendingRegisteredCreditCard({
        cardVerificationId: response.cardVerificationId,
        amount,
      });

      if (response.tds2RedirectUrl) {
        window.location.href = response.tds2RedirectUrl;
        return;
      }

      toast.success(t("クレジットカードを登録しました"));
      await navigate({ to: "/charge/credit", search: { amount } });
    } catch (error) {
      const gmoError = getGMOPGErrorInfoFromUnknown(error);
      if (gmoError) {
        showGMOPGErrorToast(gmoError, {
          fallbackMessage: t(
            "カード登録に失敗しました。カード情報をご確認いただくか、別のカードをお試しください。",
          ),
        });
        return;
      }
      showAddCardError(error);
    } finally {
      setLoading(false);
      if (shouldResetMulpay) {
        setCardElementStatuses(initialCardElementStatuses);
        onResetMulpay();
      }
    }
  };

  return (
    <>
      <Header
        title={t("クレジットカード登録")}
        onClickBack={() =>
          navigate({ to: "/charge/credit", search: { amount } })
        }
      />
      <form className={pageCss} onSubmit={submit}>
        <AvailableCreditCardBrands tenant={tenant} />

        <CardElementField
          label={t("カード番号")}
          errorMessage={cardElementStatuses.cardNumber.errorMessage}
        >
          <CardNumberElement
            id="cardNumber"
            className={cardElementCss}
            onChange={updateCardElementStatus}
            options={{
              ...cardElementOptions,
              placeholder: "1234 5678 9012 3456",
            }}
          />
        </CardElementField>
        <FormInput
          label={t("カード名義")}
          value={form.holderName}
          onChange={updateHolderName}
          autoComplete="cc-name"
          autoCapitalize="characters"
          autoCorrect="off"
          enterKeyHint="next"
          helper={t("カードに記載されている名義を半角英字で入力してください。")}
          lang="en"
          maxLength={50}
          pattern={cardHolderNamePattern}
          placeholder="TARO YAMADA"
          spellCheck={false}
          title={t("半角英字で入力してください。")}
        />
        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            w: "100%",
            maxW: "full",
          })}
        >
          <CardElementField
            label={t("有効期限")}
            errorMessage={cardElementStatuses.cardExpiry.errorMessage}
          >
            <CardExpiryElement
              id="cardExpiry"
              className={cardElementCss}
              onChange={updateCardElementStatus}
              options={{
                ...cardElementOptions,
                placeholder: "MM / YY",
              }}
            />
          </CardElementField>
          <CardElementField
            label={t("セキュリティコード")}
            errorMessage={cardElementStatuses.cardCvc.errorMessage}
            helper={t(
              "クレジットカード番号とは別に記載された3桁または4桁の番号です。",
            )}
          >
            <CardCvcElement
              id="cardCvc"
              className={cardElementCss}
              onChange={updateCardElementStatus}
              options={{
                ...cardElementOptions,
                placeholder: "123",
              }}
            />
          </CardElementField>
        </div>

        <h2
          className={css({
            fontSize: "16px",
            lineHeight: "1.5",
            color: "text.primary",
          })}
        >
          {t("請求先住所")}
        </h2>
        <FormInput
          label={t("氏名")}
          value={form.billingName}
          onChange={update("billingName")}
          autoComplete="billing name"
        />
        <FormInput
          label={t("郵便番号")}
          value={form.postCode}
          onChange={updatePostCode}
          inputMode="numeric"
          autoComplete="billing postal-code"
          placeholder="1000001"
          pattern={postCodePattern}
          maxLength={7}
          helper={t("半角数字7桁で入力してください。")}
        />
        <FormSelect
          label={t("都道府県")}
          value={form.state}
          onChange={update("state")}
          autoComplete="billing address-level1"
          options={prefectures}
          placeholder={t("選択してください")}
        />
        <FormInput
          label={t("市区町村")}
          value={form.city}
          onChange={update("city")}
          autoComplete="billing address-level2"
        />
        <FormInput
          label={t("住所")}
          value={form.line1}
          onChange={update("line1")}
          autoComplete="billing address-line1"
        />
        <FormInput
          label={t("部屋番号等")}
          value={form.line2}
          onChange={update("line2")}
          autoComplete="billing address-line2"
          required={false}
        />

        <label
          className={css({
            bg: "surface.primary",
            border: "1px solid",
            borderColor: "border.secondary",
            rounded: "12px",
            minH: "56px",
            px: "20px",
            py: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.03)",
          })}
        >
          <input
            type="checkbox"
            checked={setAsDefault}
            disabled={isFirstCard}
            onChange={update("setAsDefault")}
            className={css({ w: "20px", h: "20px" })}
          />
          <span
            className={css({
              fontSize: "16px",
              lineHeight: "1.5",
              color: "text.primary",
            })}
          >
            {t("メインカードに設定する")}
          </span>
        </label>

        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          })}
        >
          <button
            type="submit"
            className={primaryButtonCss}
            disabled={!canSubmit}
          >
            {loading ? (
              <>
                <ProgressActivity
                  className={css({ animation: "spin 1s linear infinite" })}
                />
                {t("登録中")}
              </>
            ) : (
              t("登録")
            )}
          </button>
          <button
            type="button"
            className={secondaryButtonCss}
            disabled={loading}
            onClick={() =>
              navigate({ to: "/charge/credit", search: { amount } })
            }
          >
            <ArrowBack />
            {t("もどる")}
          </button>
        </div>
      </form>
    </>
  );
}

function AvailableCreditCardBrands({ tenant }: { tenant: TenantWithFeature }) {
  const { availableBrands, notice } =
    getAvailableCreditCardBrandDisplay(tenant);

  return (
    <section
      className={css({
        bg: "surface.primary",
        border: "1px solid",
        borderColor: "border.secondary",
        rounded: "16px",
        p: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      })}
    >
      <h2 className={labelCss}>{t("利用可能クレジットカードブランド")}</h2>
      <div className={css({ display: "flex", gap: "16px" })}>
        {availableBrands.map((brand) => (
          <CardBrand key={brand} brand={brand} />
        ))}
      </div>
      {notice && (
        <p
          className={css({
            color: "text.tertiary",
            fontSize: "12px",
            lineHeight: "1.45",
          })}
        >
          {notice}
        </p>
      )}
    </section>
  );
}

function getAvailableCreditCardBrandDisplay(tenant: TenantWithFeature) {
  if (tenant.slug === "miyagi") {
    return {
      availableBrands: ["JCB", "VISA", "MC"],
      notice: null,
    };
  }

  return {
    availableBrands: ["JCB", "VISA", "MC", "AMEX", "Diners"],
    notice: null,
  };
}

type MulpayElementsProviderProps = {
  children: ReactNode;
  tenantSlug: string;
};

function MulpayElementsProvider({
  children,
  tenantSlug,
}: MulpayElementsProviderProps) {
  const [mulpayPromise, setMulpayPromise] =
    useState<Promise<MultiPayment | null> | null>(null);

  useEffect(() => {
    const promise = loadMulpayForCardToken(tenantSlug).catch(
      (error: unknown) => {
        showAddCardError(error);
        return null;
      },
    );
    setMulpayPromise(promise);
  }, [tenantSlug]);

  return <Elements multiPayment={mulpayPromise}>{children}</Elements>;
}

const cardElementCss = css({
  w: "full",
});

type CardElementFieldProps = {
  label: string;
  children: ReactNode;
  helper?: string;
  errorMessage?: string;
};

function CardElementField({
  label,
  children,
  helper,
  errorMessage,
}: CardElementFieldProps) {
  return (
    <div
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        w: "100%",
        maxW: "full",
      })}
    >
      <span className={labelCss}>{label}</span>
      {helper && <span className={helperTextCss}>{helper}</span>}
      <div
        className={cx(
          fieldCss,
          css({
            bg: "#FFFFFF",
            h: "48px",
            _focusWithin: {
              borderColor: "surface.accentPrimary",
            },
          }),
        )}
      >
        {children}
      </div>
      {errorMessage && (
        <span className={css({ fontSize: "13px", color: "#B42318" })}>
          {errorMessage}
        </span>
      )}
    </div>
  );
}

type FormInputProps = {
  label: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  autoComplete?: string;
  autoCapitalize?: React.HTMLAttributes<HTMLInputElement>["autoCapitalize"];
  autoCorrect?: string;
  enterKeyHint?: React.HTMLAttributes<HTMLInputElement>["enterKeyHint"];
  lang?: string;
  placeholder?: string;
  helper?: string;
  width?: string;
  required?: boolean;
  pattern?: string;
  maxLength?: number;
  spellCheck?: boolean;
  title?: string;
};

type FormSelectProps = {
  label: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  autoComplete?: string;
  options: readonly (readonly [string, string])[];
  placeholder: string;
  required?: boolean;
};

function FormInput({
  label,
  value,
  onChange,
  inputMode,
  autoComplete,
  autoCapitalize,
  autoCorrect,
  enterKeyHint,
  lang,
  placeholder,
  helper,
  width = "100%",
  required = true,
  pattern,
  maxLength,
  spellCheck,
  title,
}: FormInputProps) {
  return (
    <label
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        w: width,
        maxW: "full",
      })}
    >
      <span className={labelCss}>{label}</span>
      {helper && <span className={helperTextCss}>{helper}</span>}
      <input
        required={required}
        value={value}
        onChange={onChange}
        inputMode={inputMode}
        autoComplete={autoComplete}
        autoCapitalize={autoCapitalize}
        autoCorrect={autoCorrect}
        enterKeyHint={enterKeyHint}
        lang={lang}
        placeholder={placeholder}
        pattern={pattern}
        maxLength={maxLength}
        spellCheck={spellCheck}
        title={title}
        className={cx(
          inputCss,
          css({
            bg: "#FFFFFF",
            border: "1px solid",
            borderColor: "border.secondary",
            rounded: "12px",
            h: "48px",
            px: "14px",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.03)",
          }),
        )}
      />
    </label>
  );
}

function FormSelect({
  label,
  value,
  onChange,
  autoComplete = "address-level1",
  options,
  placeholder,
  required = true,
}: FormSelectProps) {
  return (
    <label
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        w: "100%",
        maxW: "full",
      })}
    >
      <span className={labelCss}>{label}</span>
      <select
        required={required}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        className={cx(
          inputCss,
          css({
            bg: "#FFFFFF",
            border: "1px solid",
            borderColor: "border.secondary",
            rounded: "12px",
            h: "48px",
            px: "14px",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.03)",
          }),
        )}
      >
        <option value="">{placeholder}</option>
        {options.map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
    </label>
  );
}

function showAddCardError(error: unknown) {
  if (error instanceof ConnectError) {
    const validationMessages = extractValidationViolationMessages(error, {
      fieldMessages: addCreditCardValidationMessages,
    });
    if (validationMessages.length > 0) {
      toast.error(
        `${t("入力内容を確認してください")}\n${validationMessages.join("\n")}`,
      );
      return;
    }
    toast.error(`${t("カード登録に失敗しました")}\n[${error.code}]`);
    return;
  }
  if (error instanceof Error) {
    if (error.message === "GMOPG_TOKEN_TIMEOUT") {
      toast.error(
        t(
          "カード情報の確認に時間がかかっています。カード情報をご確認のうえ、もう一度お試しください。",
        ),
      );
      return;
    }
    if (error.message.startsWith("GMOPG_TOKEN_")) {
      toast.error(
        t(
          "カード情報を確認できませんでした。カード番号、有効期限、セキュリティコードをご確認ください。",
        ),
      );
      return;
    }
    toast.error(`${t("カード登録に失敗しました")}\n[${error.message}]`);
    return;
  }
  toast.error(t("カード登録に失敗しました"));
}

async function getInitialBillingAddress() {
  const { fullName, cityAddress } = await getBillingProfileWithCache();
  const prefecture = prefectures.find(([, name]) =>
    cityAddress.startsWith(name),
  );

  return {
    billingName: fullName,
    state: prefecture?.[0] ?? "",
    city: prefecture ? cityAddress.slice(prefecture[1].length) : cityAddress,
  };
}
